import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Edit2, Save, Scissors, Crown, Minus, AlertTriangle, Download, Upload, FileSpreadsheet, CheckCircle2, XCircle, TrendingUp, Calendar, TagIcon, ArrowUpCircle, Settings } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, BarChart, Bar, Cell } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtCur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;

// ======================== ALLOCATION CONFIG PANEL ========================
// Pannello per configurare la ripartizione dei Costi Fissi ai clienti nella Whale Curve.
// Mostra le 3 modalità con preview live dei nuovi profit prima di salvare.
const AllocationConfigPanel = ({ clientId, whaleData, onSaved }) => {
  const [expanded, setExpanded] = useState(false);
  const [config, setConfig] = useState({ deduct_labor_from_fc: false, fc_allocation_mode: 'invoices', include_admin_comp_in_fc: false });
  const [previewMode, setPreviewMode] = useState(null); // 'invoices' | 'equal' | 'thirds_mixed'
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/clients/${clientId}/whale-curve-config`)
      .then(r => setConfig({
        deduct_labor_from_fc: !!r.data.deduct_labor_from_fc,
        fc_allocation_mode: r.data.fc_allocation_mode || 'invoices',
        include_admin_comp_in_fc: !!r.data.include_admin_comp_in_fc,
      }))
      .catch(() => {});
  }, [clientId]);

  const s = whaleData?.summary || {};
  const customers = whaleData?.customers || [];
  const totalFC = s.total_fixed_costs_period || 0;
  const totalRev = s.total_revenue || 0;
  const totalHrs = customers.reduce((a, c) => a + (c.hours || 0), 0);
  const totalInvoices = customers.reduce((a, c) => a + (c.n_invoices || 0), 0);
  const nCust = customers.length;

  // Preview: calcola profit per singolo cliente secondo la modalità scelta
  const computePreview = (mode) => {
    if (!nCust) return [];
    const third = totalFC / 3;
    return customers.map(c => {
      let alloc;
      if (mode === 'thirds_mixed') {
        const eqPart = third / nCust;
        const revPart = totalRev > 0 ? third * (c.revenue / totalRev) : 0;
        const hrsPart = totalHrs > 0 ? third * ((c.hours || 0) / totalHrs) : 0;
        alloc = eqPart + revPart + hrsPart;
      } else if (mode === 'equal') {
        alloc = totalFC / nCust;
      } else {
        alloc = totalInvoices > 0 && c.n_invoices > 0
          ? (c.n_invoices / totalInvoices) * totalFC
          : totalFC / nCust;
      }
      const profit = (c.first_margin || 0) - alloc;
      return { name: c.name, alloc, profit, revenue: c.revenue, hours: c.hours || 0 };
    });
  };

  const previewData = previewMode ? computePreview(previewMode) : null;
  const currentData = computePreview(config.fc_allocation_mode);

  const summarize = (data) => {
    const head = data.filter(x => x.profit > 0).length;
    const tail = data.filter(x => x.profit < 0).length;
    const body = data.length - head - tail;
    const totProfit = data.reduce((a, x) => a + x.profit, 0);
    return { head, body, tail, totProfit };
  };

  const currentSum = summarize(currentData);
  const previewSum = previewData ? summarize(previewData) : null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...config, fc_allocation_mode: previewMode || config.fc_allocation_mode };
      await axios.put(`${API}/clients/${clientId}/whale-curve-config`, payload);
      toast.success("Configurazione salvata");
      setPreviewMode(null);
      onSaved?.();
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally { setSaving(false); }
  };

  const MODE_LABELS = {
    invoices: 'Per numero fatture (proxy interventi)',
    equal: 'Uniforme (FC diviso egualmente per tutti)',
    thirds_mixed: 'Mista 1/3: uniforme + % fatturato + % ore lavorate',
  };

  return (
    <Card className="border-slate-300">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Configurazione avanzata ripartizione Costi Fissi
          </CardTitle>
          <Button size="sm" variant="ghost" data-testid="toggle-alloc-config">{expanded ? '▲ Chiudi' : '▼ Apri'}</Button>
        </div>
        {!expanded && (
          <div className="text-xs text-slate-500 mt-1">
            Modalità attiva: <strong>{MODE_LABELS[config.fc_allocation_mode]}</strong>
            {config.deduct_labor_from_fc && <span> · Personale dedotto dai FC</span>}
            {config.include_admin_comp_in_fc && <span> · Compensi Soci inclusi</span>}
          </div>
        )}
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          {/* Base FC calcolata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="bg-slate-50 border rounded p-2">
              <div className="text-slate-500">FC da ripartire</div>
              <div className="text-lg font-bold">{fmtCur(totalFC)}</div>
              <div className="text-slate-400">{s.deduct_labor_from_fc ? '(post-deduzione labor)' : ''}</div>
            </div>
            <div className="bg-slate-50 border rounded p-2">
              <div className="text-slate-500">N. clienti</div>
              <div className="text-lg font-bold">{nCust}</div>
            </div>
            <div className="bg-slate-50 border rounded p-2">
              <div className="text-slate-500">Ore totali</div>
              <div className="text-lg font-bold">{totalHrs.toFixed(0)}h</div>
            </div>
            <div className="bg-slate-50 border rounded p-2">
              <div className="text-slate-500">Fatture totali</div>
              <div className="text-lg font-bold">{totalInvoices}</div>
            </div>
          </div>

          {/* Toggle deduzioni */}
          <div className="flex flex-wrap gap-4 text-sm border-b pb-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-deduct-labor">
              <input type="checkbox" checked={config.deduct_labor_from_fc}
                onChange={e => setConfig(c => ({ ...c, deduct_labor_from_fc: e.target.checked }))} />
              <span>Deduci <strong>Personale dipendente</strong> dai FC (evita doppio conteggio col labor)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-admin-comp">
              <input type="checkbox" checked={config.include_admin_comp_in_fc}
                onChange={e => setConfig(c => ({ ...c, include_admin_comp_in_fc: e.target.checked }))} />
              <span>Includi <strong>Compensi Soci</strong> nei FC da ripartire</span>
            </label>
          </div>

          {/* 3 modalità in cards */}
          <div>
            <div className="text-sm font-semibold mb-2">Modalità di ripartizione</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {['invoices', 'equal', 'thirds_mixed'].map(mode => {
                const data = computePreview(mode);
                const sum = summarize(data);
                const isActive = config.fc_allocation_mode === mode;
                const isPreview = previewMode === mode;
                return (
                  <div key={mode}
                    className={`border-2 rounded-lg p-3 cursor-pointer transition ${isPreview ? 'border-sky-500 bg-sky-50' : isActive ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-400'}`}
                    onClick={() => setPreviewMode(isPreview ? null : mode)}
                    data-testid={`alloc-mode-${mode}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-bold">{MODE_LABELS[mode]}</div>
                      {isActive && <Badge className="bg-emerald-200 text-emerald-800 text-[9px]">ATTIVA</Badge>}
                      {isPreview && !isActive && <Badge className="bg-sky-200 text-sky-800 text-[9px]">ANTEPRIMA</Badge>}
                    </div>
                    <div className="text-xs text-slate-600 space-y-0.5">
                      <div>Testa/Corpo/Coda: <strong>{sum.head}/{sum.body}/{sum.tail}</strong></div>
                      <div>Profit tot: <strong className={sum.totProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}>{fmtCur(sum.totProfit)}</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Preview vs corrente */}
          {previewMode && previewMode !== config.fc_allocation_mode && (
            <div className="bg-sky-50 border-2 border-sky-300 rounded-lg p-3">
              <div className="text-sm font-bold text-sky-900 mb-2">📊 Preview: {MODE_LABELS[previewMode]}</div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="text-slate-500">Modalità attiva ({MODE_LABELS[config.fc_allocation_mode]})</div>
                  <div>Testa {currentSum.head} · Corpo {currentSum.body} · Coda <strong className="text-red-700">{currentSum.tail}</strong></div>
                  <div>Profit tot: <strong>{fmtCur(currentSum.totProfit)}</strong></div>
                </div>
                <div>
                  <div className="text-sky-700 font-semibold">Preview con nuova modalità</div>
                  <div>Testa {previewSum.head} · Corpo {previewSum.body} · Coda <strong className="text-red-700">{previewSum.tail}</strong> <span className={previewSum.tail > currentSum.tail ? 'text-red-600' : 'text-emerald-600'}>({previewSum.tail - currentSum.tail >= 0 ? '+' : ''}{previewSum.tail - currentSum.tail})</span></div>
                  <div>Profit tot: <strong>{fmtCur(previewSum.totProfit)}</strong> <span className={previewSum.totProfit - currentSum.totProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}>({previewSum.totProfit - currentSum.totProfit >= 0 ? '+' : ''}{fmtCur(previewSum.totProfit - currentSum.totProfit)})</span></div>
                </div>
              </div>
              {/* Top 5 impatti maggiori */}
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-sky-700 font-semibold">Vedi impatto sui top 5 clienti</summary>
                <table className="w-full mt-2">
                  <thead className="bg-white"><tr>
                    <th className="text-left p-1">Cliente</th>
                    <th className="text-right p-1">Alloc attuale</th>
                    <th className="text-right p-1">Alloc nuova</th>
                    <th className="text-right p-1">Δ Profit</th>
                  </tr></thead>
                  <tbody>
                    {currentData.slice(0, 5).map((c, i) => {
                      const p = previewData.find(x => x.name === c.name);
                      const deltaProfit = (p?.profit || 0) - c.profit;
                      return (
                        <tr key={i} className="border-b border-sky-100">
                          <td className="p-1">{c.name.slice(0, 30)}</td>
                          <td className="text-right p-1 tabular-nums">{fmtCur(c.alloc)}</td>
                          <td className="text-right p-1 tabular-nums font-semibold">{fmtCur(p?.alloc || 0)}</td>
                          <td className={`text-right p-1 tabular-nums font-bold ${deltaProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{deltaProfit >= 0 ? '+' : ''}{fmtCur(deltaProfit)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </details>
            </div>
          )}

          {/* Bottone salva */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={() => setPreviewMode(null)} disabled={!previewMode}>
              Annulla preview
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="btn-save-alloc-config"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? "Salvo…" : previewMode && previewMode !== config.fc_allocation_mode ? "Applica nuova modalità" : "Salva configurazione"}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// ======================== CUSTOMER FORM DIALOG ========================
const CustomerFormDialog = ({ open, onClose, onSave, customer, hourlyRate }) => {
  const [form, setForm] = useState({ name: '', revenue: '', variable_costs: '', hours: '', notes: '' });
  useEffect(() => {
    if (customer) setForm({ name: customer.name, revenue: customer.revenue || '', variable_costs: customer.variable_costs || '', hours: customer.hours || '', notes: customer.notes || '' });
    else setForm({ name: '', revenue: '', variable_costs: '', hours: '', notes: '' });
  }, [customer, open]);

  const rev = parseFloat(form.revenue) || 0;
  const vc = parseFloat(form.variable_costs) || 0;
  const hrs = parseFloat(form.hours) || 0;
  const hr = hourlyRate || 25;
  const laborCost = hrs * hr;
  const firstMargin = rev - vc - laborCost;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{customer ? 'Modifica' : 'Aggiungi'} Cliente</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome cliente</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="es. Mario Rossi Srl" data-testid="customer-name-input" /></div>
          <div><Label>Fatturato (EUR)</Label><Input type="number" value={form.revenue} onChange={e => setForm(f => ({ ...f, revenue: e.target.value }))} placeholder="0" data-testid="customer-revenue-input" /></div>
          <div><Label>Costi variabili prodotti (EUR)</Label><Input type="number" value={form.variable_costs} onChange={e => setForm(f => ({ ...f, variable_costs: e.target.value }))} placeholder="0" data-testid="customer-variable-costs-input" /><p className="text-xs text-muted-foreground mt-0.5">Materiali, fornitori, merci acquistate per questo cliente</p></div>
          <div><Label>Ore di lavoro</Label><Input type="number" step="0.5" value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} placeholder="0" data-testid="customer-hours-input" /><p className="text-xs text-muted-foreground mt-0.5">Costo orario: {fmtCur(hr)}/h → Costo manodopera: {fmtCur(laborCost)}</p></div>
          <div><Label>Note</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Opzionale" /></div>
          {/* Margin preview */}
          <div className={`p-3 rounded-lg border-2 ${firstMargin >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            <div className="grid grid-cols-2 gap-2 text-center text-xs">
              <div><div className="text-muted-foreground">Fatturato</div><div className="font-bold">{fmtCur(rev)}</div></div>
              <div><div className="text-muted-foreground">Costi var. + Manodopera</div><div className="font-bold">{fmtCur(vc + laborCost)}</div></div>
            </div>
            <div className="text-center mt-2 pt-2 border-t">
              <div className="text-[10px] text-muted-foreground uppercase">Primo Margine (prima dei costi fissi)</div>
              <div className={`font-black text-lg ${firstMargin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtCur(firstMargin)}</div>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground text-center">I costi fissi verranno allocati automaticamente in proporzione al fatturato</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => { const payload = { name: form.name, revenue: parseFloat(form.revenue) || 0, variable_costs: parseFloat(form.variable_costs) || 0, hours: parseFloat(form.hours) || 0, direct_costs: 0, allocated_costs: 0, cost_allocation_mode: 'revenue', allocation_pct: 0, notes: form.notes || '' }; onSave(payload); }} disabled={!form.name || !form.revenue} data-testid="save-customer-btn"><Save className="h-4 w-4 mr-1" />Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ======================== IMPORT DIALOG ========================
const ImportDialog = ({ open, onClose, clientId, onImportDone, selectedYear }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (!open) { setFile(null); setResult(null); } }, [open]);

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/clients/${clientId}/customers/import?year=${selectedYear || ''}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setResult(res.data);
      onImportDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore importazione");
    } finally { setImporting(false); }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/clients/${clientId}/customers/template`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_clienti.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Template scaricato");
    } catch { toast.error("Errore download template"); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-600" /> Importa Clienti da Excel</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!result ? (
            <>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm space-y-2">
                <p className="font-semibold text-blue-900">Come funziona:</p>
                <ul className="list-disc ml-4 text-blue-800 space-y-1">
                  <li>Scarica il <strong>template Excel</strong> e compilalo con i dati</li>
                  <li>Colonne: Nome, Fatturato, Costi Variabili, Ore di Lavoro</li>
                  <li>Il sistema calcola automaticamente manodopera e costi fissi allocati</li>
                  <li>Clienti con lo <strong>stesso nome</strong> vengono aggiornati (non duplicati)</li>
                </ul>
              </div>
              <Button variant="outline" className="w-full justify-start gap-2" onClick={downloadTemplate} data-testid="download-template-btn">
                <Download className="h-4 w-4 text-blue-600" /> Scarica Template Excel
              </Button>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
                <input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files[0])} className="hidden" id="excel-upload" data-testid="excel-file-input" />
                <label htmlFor="excel-upload" className="cursor-pointer">
                  {file ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileSpreadsheet className="h-8 w-8 text-emerald-600" />
                      <div className="text-left">
                        <p className="font-semibold text-sm">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm font-medium">Clicca per selezionare il file Excel</p>
                      <p className="text-xs text-muted-foreground mt-1">Formati: .xlsx</p>
                    </div>
                  )}
                </label>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="font-bold text-emerald-900">Importazione completata</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-white rounded p-2">
                    <div className="text-2xl font-black text-emerald-700">{result.created}</div>
                    <div className="text-[10px] uppercase text-muted-foreground">Creati</div>
                  </div>
                  <div className="bg-white rounded p-2">
                    <div className="text-2xl font-black text-blue-700">{result.updated}</div>
                    <div className="text-[10px] uppercase text-muted-foreground">Aggiornati</div>
                  </div>
                  <div className="bg-white rounded p-2">
                    <div className="text-2xl font-black">{result.total_processed}</div>
                    <div className="text-[10px] uppercase text-muted-foreground">Totale</div>
                  </div>
                </div>
                {result.calculation_info && (
                  <div className="mt-3 pt-3 border-t border-emerald-200 text-xs text-emerald-800 space-y-1">
                    <div className="font-semibold">Calcolo automatico applicato:</div>
                    <div>Costo orario: {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(result.calculation_info.hourly_rate)}/h</div>
                    <div>Costi fissi dal CE ({selectedYear}): {fmtCur(result.calculation_info.total_fixed_costs)}</div>
                    <div>Ore totali clienti: {result.calculation_info.total_hours}h</div>
                    <div>Metodo allocazione: {result.calculation_info.allocation_method}</div>
                  </div>
                )}
              </div>
              {result.errors?.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                  <div className="flex items-center gap-1 mb-1 text-red-800 font-semibold"><XCircle className="h-4 w-4" /> Errori:</div>
                  <ul className="text-red-700 text-xs space-y-0.5">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button onClick={handleImport} disabled={!file || importing} data-testid="import-excel-btn">
                {importing ? 'Importazione...' : <><Upload className="h-4 w-4 mr-1" /> Importa</>}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>Chiudi</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ======================== MAIN WHALE CURVE COMPONENT ========================
export const WhaleCurvePage = ({ clientId, clientName, selectedYear }) => {
  const [customers, setCustomers] = useState([]);
  const [whaleData, setWhaleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [periodoAnno, setPeriodoAnno] = useState(String(selectedYear || new Date().getFullYear()));
  const [periodoMeseDa, setPeriodoMeseDa] = useState("1");
  const [periodoMeseA, setPeriodoMeseA] = useState(String(new Date().getMonth() || 12));

  const MESI = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

  const fetchAll = useCallback(async () => {
    try {
      const params = { year: periodoAnno, month_from: periodoMeseDa, month_to: periodoMeseA };
      const [custRes, whaleRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/customers`),
        axios.get(`${API}/clients/${clientId}/whale-curve`, { params }),
      ]);
      setCustomers(custRes.data.customers);
      setWhaleData(whaleRes.data);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }, [clientId, periodoAnno, periodoMeseDa, periodoMeseA]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveCustomer = async (form) => {
    try {
      if (editCustomer) await axios.put(`${API}/clients/${clientId}/customers/${editCustomer.id}`, form);
      else await axios.post(`${API}/clients/${clientId}/customers`, form);
      toast.success(editCustomer ? 'Aggiornato' : 'Aggiunto');
      setShowForm(false); setEditCustomer(null); fetchAll();
    } catch { toast.error("Errore"); }
  };

  const deleteCustomer = async (id) => {
    try { await axios.delete(`${API}/clients/${clientId}/customers/${id}`); toast.success("Eliminato"); fetchAll(); } catch { toast.error("Errore"); }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Caricamento...</div>;

  const s = whaleData?.summary;
  const zones = whaleData?.zones || {};
  const getZone = (id) => zones.head?.includes(id) ? 'head' : zones.body?.includes(id) ? 'body' : 'tail';
  const zoneBadge = (zone) => {
    if (zone === 'head') return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]"><Crown className="h-3 w-3 mr-0.5" />Testa</Badge>;
    if (zone === 'body') return <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]"><Minus className="h-3 w-3 mr-0.5" />Corpo</Badge>;
    return <Badge className="bg-red-100 text-red-800 border-red-300 text-[10px]"><Scissors className="h-3 w-3 mr-0.5" />Coda</Badge>;
  };

  // Compute zone boundaries for chart
  const zoneBounds = (() => {
    if (!whaleData?.customers?.length) return { headEnd: 0, bodyEnd: 0, tailEnd: 100 };
    const custs = whaleData.customers;
    let headEnd = 0, bodyEnd = 0;
    for (const c of custs) {
      const z = getZone(c.id);
      if (z === 'head') headEnd = c.pct_customers;
      if (z === 'head' || z === 'body') bodyEnd = c.pct_customers;
    }
    return { headEnd, bodyEnd, tailEnd: custs[custs.length - 1]?.pct_customers || 100 };
  })();

  // Group customers by zone for table sections
  const groupedCustomers = (() => {
    const custs = whaleData?.customers || [];
    const head = [], body = [], tail = [];
    custs.forEach(c => {
      const z = getZone(c.id);
      if (z === 'head') head.push(c);
      else if (z === 'body') body.push(c);
      else tail.push(c);
    });
    return { head, body, tail };
  })();

  const handlePrint = () => {
    document.body.classList.add('printing-section');
    setTimeout(() => window.print(), 200);
    window.addEventListener('afterprint', () => document.body.classList.remove('printing-section'), { once: true });
  };

  return (
    <div className="space-y-4">
      {/* Print-only header - hidden on screen, visible in print via CSS */}
      <div className="print-header">
        <div className="print-header-left">
          <h1 style={{fontSize:'22px', fontWeight:800, color:'#1e293b', margin:0}}>Whale Curve — Analisi Clienti</h1>
          <p style={{fontSize:'11px', color:'#64748b', margin:'2px 0 0'}}>Analisi di Kaplan & Cooper: redditività per cliente con allocazione costi fissi (ABC)</p>
        </div>
        <div className="print-header-right" style={{textAlign:'right'}}>
          <div style={{fontSize: '13px', fontWeight: 700}}>{clientName || 'Cliente'}</div>
          <div style={{fontSize:'11px', color:'#64748b'}}>Periodo: {MESI[parseInt(periodoMeseDa)]}{periodoMeseDa !== periodoMeseA ? ` - ${MESI[parseInt(periodoMeseA)]}` : ''} {periodoAnno}</div>
          <div style={{fontSize:'11px', color:'#64748b'}}>Generato il {new Date().toLocaleDateString('it-IT')}</div>
        </div>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-2 no-print">
        <div>
          <h2 className="text-lg font-bold">Whale Curve - Curva della Balena</h2>
          <p className="text-sm text-muted-foreground">Analisi di Kaplan & Cooper: il 20-30% dei clienti genera il 150%+ del margine</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex items-center gap-1.5 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Periodo dati:</span>
            <Select value={periodoMeseDa} onValueChange={setPeriodoMeseDa}>
              <SelectTrigger className="w-[110px] h-8" data-testid="whale-month-from"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MESI.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-muted-foreground">-</span>
            <Select value={periodoMeseA} onValueChange={setPeriodoMeseA}>
              <SelectTrigger className="w-[110px] h-8" data-testid="whale-month-to"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MESI.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={periodoAnno} onValueChange={setPeriodoAnno}>
              <SelectTrigger className="w-[80px] h-8" data-testid="whale-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" variant="outline" onClick={handlePrint} data-testid="print-whale-btn"><Download className="h-4 w-4 mr-1" />Esporta PDF</Button>
          <Button size="sm" variant="outline" onClick={async () => {
            try {
              const token = localStorage.getItem('token');
              const res = await axios.get(`${API}/clients/${clientId}/customers/export`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url; a.download = 'clienti_whale_curve.xlsx'; a.click();
              window.URL.revokeObjectURL(url);
              toast.success("Excel esportato — modifica e ricarica per aggiornare i dati");
            } catch { toast.error("Errore export"); }
          }} data-testid="export-customers-btn"><Download className="h-4 w-4 mr-1" />Esporta Excel</Button>
          <Button size="sm" variant="outline" onClick={() => setShowImport(true)} data-testid="open-import-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Importa Excel</Button>
          <Button size="sm" onClick={() => { setEditCustomer(null); setShowForm(true); }} data-testid="add-customer-btn"><Plus className="h-4 w-4 mr-1" />Aggiungi Cliente</Button>
        </div>
      </div>

      {customers.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <div className="text-5xl mb-3">🐳</div>
          <h3 className="text-lg font-semibold">Inserisci i clienti finali</h3>
          <p className="text-muted-foreground max-w-md mx-auto mb-4">Per ogni cliente, inserisci fatturato, costi variabili e ore di lavoro. L'app calcolera il costo manodopera e allochera i costi fissi dal CE in base al fatturato.</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => setShowImport(true)} data-testid="empty-import-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Importa da Excel</Button>
            <Button onClick={() => { setEditCustomer(null); setShowForm(true); }}><Plus className="h-4 w-4 mr-1" />Aggiungi Manualmente</Button>
          </div>
        </CardContent></Card>
      ) : (<>
        {whaleData?.loss_mode && (
          <Card className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <div className="text-2xl">⚠️</div>
                <div className="flex-1">
                  <div className="font-semibold text-amber-900 mb-1">Modalità Perdita Strutturale Attiva</div>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    L'azienda è in perdita: i costi fissi (ripartiti in base al numero di fatture emesse) superano il margine di contribuzione di tutti i clienti.
                    <br />
                    <strong>Classificazione (per contribuzione assoluta ai costi fissi):</strong>
                    <span className="inline-flex items-center gap-1 ml-1">
                      <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded text-[11px] font-semibold">TESTA</span>
                    = top 20% (key-account che pagano di più verso i Costi Fissi),
                    </span>
                    <span className="inline-flex items-center gap-1 ml-1">
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[11px] font-semibold">CORPO</span>
                      = 60% centrali,
                    </span>
                    <span className="inline-flex items-center gap-1 ml-1">
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-800 rounded text-[11px] font-semibold">CODA</span>
                      = bottom 20% + chi non copre nemmeno i propri costi diretti.
                    </span>
                    <br />
                    <strong>Simulazione prezzo:</strong> target <strong>+5% di margine netto</strong> (obiettivo di salute aziendale) — la tabella sottostante mostra l'aumento di prezzo necessario su ciascun cliente per uscire dalla perdita.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Summary cards */}
        {s && (<>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card><CardContent className="pt-3 pb-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">Clienti totali</div><div className="text-2xl font-black">{s.total_customers}</div></CardContent></Card>
            <Card className="border-emerald-200"><CardContent className="pt-3 pb-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">Testa (profittevoli)</div><div className="text-2xl font-black text-emerald-700">{s.head_count}</div></CardContent></Card>
            <Card className="border-red-200"><CardContent className="pt-3 pb-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">Coda (distruggono)</div><div className="text-2xl font-black text-red-700">{s.tail_count}</div></CardContent></Card>
            <Card><CardContent className="pt-3 pb-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">Margine potenziale</div><div className="text-lg font-black text-emerald-700">{fmtCur(s.potential_profit)}</div></CardContent></Card>
            <Card className="border-red-200"><CardContent className="pt-3 pb-2 text-center"><div className="text-[10px] uppercase text-muted-foreground">Margine distrutto</div><div className="text-lg font-black text-red-700">{fmtCur(s.profit_destroyed)} ({fmtPct(s.profit_pct_lost)})</div></CardContent></Card>
          </div>
          {s.total_fixed_costs_period > 0 && (
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
                  <span className="font-semibold text-slate-700">Allocazione costi fissi per fatturato - {MESI[parseInt(periodoMeseDa)]}{periodoMeseDa !== periodoMeseA ? ` / ${MESI[parseInt(periodoMeseA)]}` : ''} {periodoAnno}:</span>
                  <span>Costo orario: <strong>{new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(s.hourly_rate)}</strong>/h</span>
                  <span>Ore totali: <strong>{s.total_hours}h</strong></span>
                  <span>Costi fissi periodo: <strong>{fmtCur(s.total_fixed_costs_period)}</strong></span>
                  <span>Allocati ai clienti: <strong className={Math.abs((s.total_fixed_costs_period || 0) - (s.total_fixed_costs_allocated || 0)) < 1 ? 'text-emerald-700' : 'text-red-700'}>{fmtCur(s.total_fixed_costs_allocated)}</strong></span>
                  <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      await axios.post(`${API}/clients/${clientId}/customers/recalculate?year=${selectedYear || ''}`, null, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success("Ricalcolo completato"); fetchAll();
                    } catch { toast.error("Errore ricalcolo"); }
                  }} data-testid="recalculate-btn">Ricalcola</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>)}

        {/* CONFIG PANEL ALLOCAZIONE FC */}
        <AllocationConfigPanel clientId={clientId} whaleData={whaleData} onSaved={fetchAll} />

        {/* WHALE CURVE CHART */}
        {whaleData?.curve_data?.length > 0 && (
          <Card className="print-avoid-break">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span className="text-2xl">🐳</span> Whale Curve
              </CardTitle>
              <CardDescription className="flex items-center gap-4 flex-wrap">
                <span>Margine cumulato per zona:</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-200 inline-block"></span> Testa</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-200 inline-block"></span> Corpo</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 inline-block"></span> Coda</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={whaleData.curve_data} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
                  <defs>
                    <linearGradient id="whaleGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  {/* Zone background areas */}
                  {zoneBounds.headEnd > 0 && (
                    <ReferenceArea x1={0} x2={zoneBounds.headEnd} fill="#10B981" fillOpacity={0.08} />
                  )}
                  {zoneBounds.bodyEnd > zoneBounds.headEnd && (
                    <ReferenceArea x1={zoneBounds.headEnd} x2={zoneBounds.bodyEnd} fill="#F59E0B" fillOpacity={0.08} />
                  )}
                  {zoneBounds.tailEnd > zoneBounds.bodyEnd && (
                    <ReferenceArea x1={zoneBounds.bodyEnd} x2={zoneBounds.tailEnd} fill="#EF4444" fillOpacity={0.1} />
                  )}
                  <XAxis dataKey="pct_customers" tickFormatter={v => `${Math.round(v)}%`} label={{ value: whaleData?.loss_mode ? '% Clienti (dal più grande al più piccolo per contribuzione ai costi fissi)' : '% Clienti (dal più al meno profittevole)', position: 'bottom', offset: 15, style: { fontSize: 11 } }} />
                  <YAxis tickFormatter={v => `${(v/1000).toFixed(0)}k`} label={{ value: 'Margine cumulato (EUR)', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11 } }} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    const cust = whaleData.customers?.find(c => c.rank === d.rank);
                    const z = cust ? getZone(cust.id) : 'body';
                    const zLabel = z === 'head' ? 'Testa' : z === 'body' ? 'Corpo' : 'Coda';
                    const zColor = z === 'head' ? 'text-emerald-700' : z === 'body' ? 'text-amber-700' : 'text-red-700';
                    return (
                      <div className="bg-white border rounded-lg shadow-lg p-3 text-xs">
                        <div className="font-bold mb-1">{d.name}</div>
                        <div className={`font-semibold ${zColor} mb-1`}>Zona: {zLabel}</div>
                        <div>Margine: <span className={d.profit >= 0 ? 'text-emerald-700 font-bold' : 'text-red-700 font-bold'}>{fmtCur(d.profit)}</span></div>
                        <div>Cumulato: {fmtCur(d.cumulative_profit)}</div>
                        <div>{Math.round(d.pct_customers)}% dei clienti</div>
                      </div>
                    );
                  }} />
                  <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="3 3" />
                  {s && <ReferenceLine y={s.max_cumulative_profit} stroke="#10B981" strokeDasharray="5 5" label={{ value: `Picco: ${fmtCur(s.max_cumulative_profit)}`, position: 'right', style: { fontSize: 10, fill: '#10B981' } }} />}
                  <Area type="monotone" dataKey="cumulative_profit" stroke="#06B6D4" strokeWidth={3} fill="url(#whaleGrad)" dot={({ cx, cy, payload }) => {
                    const cust = whaleData.customers?.find(c => c.rank === payload.rank);
                    const z = cust ? getZone(cust.id) : 'body';
                    const color = z === 'head' ? '#10B981' : z === 'body' ? '#F59E0B' : '#EF4444';
                    return <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={2} />;
                  }} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Action insight */}
        {s && s.tail_count > 0 && (
          <Card className="border-2 border-red-300 bg-gradient-to-r from-red-50 via-white to-red-50">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center"><Scissors className="h-6 w-6 text-red-700" /></div>
                <div>
                  <h3 className="font-bold text-red-900">Taglia la coda della balena</h3>
                  <p className="text-sm text-red-800 mt-1">
                    <strong>{s.tail_count} client{s.tail_count > 1 ? 'i' : 'e'}</strong> nella coda {s.tail_count > 1 ? 'stanno distruggendo' : 'sta distruggendo'} <strong>{fmtCur(s.profit_destroyed)}</strong> di margine.
                    {s.potential_profit > 0 && <> Senza di {s.tail_count > 1 ? 'loro' : 'esso'}, il margine salirebbe da <strong>{fmtCur(s.total_profit)}</strong> a <strong>{fmtCur(s.potential_profit)}</strong> ({fmtPct(s.profit_pct_lost)} in piu).</>}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profit waterfall bar chart */}
        {whaleData?.customers?.length > 0 && (
          <Card className="print-page-break print-avoid-break">
            <CardHeader><CardTitle className="text-base">Margine per Cliente</CardTitle><CardDescription>Verde = Testa, Ambra = Corpo, Rosso = Coda</CardDescription></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(200, whaleData.customers.length * 35)}>
                <BarChart data={whaleData.customers} layout="vertical" margin={{ left: 120 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={v => fmtCur(v)} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip formatter={v => fmtCur(v)} />
                  <ReferenceLine x={0} stroke="#94A3B8" />
                  <Bar dataKey="profit" name="Margine" radius={[0, 4, 4, 0]}>
                    {whaleData.customers.map((c, i) => {
                      const z = getZone(c.id);
                      const color = z === 'head' ? '#10B981' : z === 'body' ? '#F59E0B' : '#EF4444';
                      return <Cell key={i} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Customer table grouped by zone */}
        <div className="print-page-break" />
        <Card>
          <CardHeader><CardTitle className="text-base">Dettaglio Clienti per Zona</CardTitle><CardDescription>Fatturato - Costi var. - Manodopera = Primo Margine - Costi fissi allocati = Margine netto</CardDescription></CardHeader>
          <CardContent className="overflow-x-auto max-h-[75vh] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 z-20 bg-slate-100 shadow-[0_2px_0_rgba(0,0,0,0.08)]">
                <TableRow className="border-b-2">
                  <TableHead className="w-8 text-[10px] font-bold text-slate-700 bg-slate-100">#</TableHead>
                  <TableHead className="text-[10px] font-bold text-slate-700 bg-slate-100">Cliente</TableHead>
                  <TableHead className="text-[10px] font-bold text-slate-700 bg-slate-100">Zona</TableHead>
                  <TableHead className="text-right text-[10px] font-bold text-slate-700 bg-slate-100">Fatturato</TableHead>
                  <TableHead className="text-right text-[10px] font-bold text-slate-700 bg-slate-100">Costi var.</TableHead>
                  <TableHead className="text-right text-[10px] font-bold text-slate-700 bg-slate-100">Ore</TableHead>
                  <TableHead className="text-right text-[10px] font-bold text-slate-700 bg-slate-100">Manodop.</TableHead>
                  <TableHead className="text-right text-[10px] font-bold text-slate-700 bg-slate-100">1° Margine</TableHead>
                  <TableHead className="text-right text-[10px] font-bold text-slate-700 bg-slate-100">C. fissi</TableHead>
                  <TableHead className="text-right text-[10px] font-bold text-slate-700 bg-slate-100">Margine</TableHead>
                  <TableHead className="text-right text-[10px] font-bold text-slate-700 bg-slate-100">%</TableHead>
                  <TableHead className="bg-slate-100"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* TESTA */}
                {groupedCustomers.head.length > 0 && (
                  <>
                  <TableRow className="bg-emerald-100/60 hover:bg-emerald-100/60">
                    <TableCell colSpan={12} className="py-2">
                      <div className="flex items-center gap-2 font-bold text-emerald-800 text-sm">
                        <Crown className="h-4 w-4" /> TESTA — {groupedCustomers.head.length} client{groupedCustomers.head.length > 1 ? 'i' : 'e'} che generano margine
                      </div>
                    </TableCell>
                  </TableRow>
                  </>
                )}
                {groupedCustomers.head.map((c) => (
                  <TableRow key={c.id} className="bg-emerald-50/40 border-l-4 border-l-emerald-400">
                    <TableCell className="font-bold text-muted-foreground">{c.rank}</TableCell>
                    <TableCell className="font-semibold text-sm">{c.name}</TableCell>
                    <TableCell>{zoneBadge('head')}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.variable_costs)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{(c.hours || 0).toFixed(0)}h</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.labor_cost)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.first_margin)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-muted-foreground">{fmtCur(c.allocated_costs)}</TableCell>
                    <TableCell className="text-right tabular-nums font-bold text-emerald-700">{fmtCur(c.profit)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtPct(c.margin_pct)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEditCustomer(c); setShowForm(true); }} data-testid={`edit-customer-${c.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteCustomer(c.id)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* CORPO */}
                {groupedCustomers.body.length > 0 && (
                  <>
                  <TableRow className="bg-amber-100/60 hover:bg-amber-100/60">
                    <TableCell colSpan={12} className="py-2">
                      <div className="flex items-center gap-2 font-bold text-amber-800 text-sm">
                        <Minus className="h-4 w-4" /> CORPO — {groupedCustomers.body.length} client{groupedCustomers.body.length > 1 ? 'i' : 'e'} vicini al picco
                      </div>
                    </TableCell>
                  </TableRow>
                  </>
                )}
                {groupedCustomers.body.map((c) => (
                  <TableRow key={c.id} className="bg-amber-50/40 border-l-4 border-l-amber-400">
                    <TableCell className="font-bold text-muted-foreground">{c.rank}</TableCell>
                    <TableCell className="font-semibold text-sm">{c.name}</TableCell>
                    <TableCell>{zoneBadge('body')}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.variable_costs)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{(c.hours || 0).toFixed(0)}h</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.labor_cost)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.first_margin)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-muted-foreground">{fmtCur(c.allocated_costs)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-bold ${c.profit >= 0 ? 'text-amber-700' : 'text-red-700'}`}>{fmtCur(c.profit)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtPct(c.margin_pct)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEditCustomer(c); setShowForm(true); }} data-testid={`edit-customer-${c.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteCustomer(c.id)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* CODA */}
                {groupedCustomers.tail.length > 0 && (
                  <>
                  <TableRow className="bg-red-100/60 hover:bg-red-100/60">
                    <TableCell colSpan={12} className="py-2">
                      <div className="flex items-center gap-2 font-bold text-red-800 text-sm">
                        <Scissors className="h-4 w-4" /> CODA — {groupedCustomers.tail.length} client{groupedCustomers.tail.length > 1 ? 'i' : 'e'} che distruggono margine
                      </div>
                    </TableCell>
                  </TableRow>
                  </>
                )}
                {groupedCustomers.tail.map((c) => (
                  <TableRow key={c.id} className="bg-red-50/40 border-l-4 border-l-red-400">
                    <TableCell className="font-bold text-muted-foreground">{c.rank}</TableCell>
                    <TableCell className="font-semibold text-sm">{c.name}</TableCell>
                    <TableCell>{zoneBadge('tail')}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.variable_costs)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{(c.hours || 0).toFixed(0)}h</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.labor_cost)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtCur(c.first_margin)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs text-muted-foreground">{fmtCur(c.allocated_costs)}</TableCell>
                    <TableCell className="text-right tabular-nums font-bold text-red-700">{fmtCur(c.profit)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{fmtPct(c.margin_pct)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEditCustomer(c); setShowForm(true); }} data-testid={`edit-customer-${c.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteCustomer(c.id)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Best Clients Analysis & Body-to-Head Simulation */}
        {groupedCustomers.head.length > 0 && groupedCustomers.body.length > 0 && (() => {
          const head = groupedCustomers.head;
          const body = groupedCustomers.body;

          // Head: weighted metrics
          const headTotalRevenue = head.reduce((s, c) => s + (c.revenue || 0), 0);
          const headTotalProfit = head.reduce((s, c) => s + (c.profit || 0), 0);
          const headWeightedMargin = headTotalRevenue > 0 ? (headTotalProfit / headTotalRevenue * 100) : 0;
          const headAvgRevenue = headTotalRevenue / head.length;
          const headAvgProfit = headTotalProfit / head.length;

          // Body: weighted metrics
          const bodyTotalRevenue = body.reduce((s, c) => s + (c.revenue || 0), 0);
          const bodyTotalProfit = body.reduce((s, c) => s + (c.profit || 0), 0);
          const bodyWeightedMargin = bodyTotalRevenue > 0 ? (bodyTotalProfit / bodyTotalRevenue * 100) : 0;
          const bodyAvgRevenue = bodyTotalRevenue / body.length;
          const bodyAvgProfit = bodyTotalProfit / body.length;

          // Simulation: each body client reaches head's avg revenue and avg profit
          const bodySimulatedRevenue = headAvgRevenue * body.length;
          const bodySimulatedProfit = headAvgProfit * body.length;
          const bodyRevenueGain = bodySimulatedRevenue - bodyTotalRevenue;
          const bodyProfitGain = bodySimulatedProfit - bodyTotalProfit;

          // Totals
          const totalProfit = (whaleData?.customers || []).reduce((s, c) => s + (c.profit || 0), 0);
          const simulatedTotalProfit = totalProfit + bodyProfitGain;

          return (
            <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 via-white to-cyan-50/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" /> E se i clienti del Corpo diventassero come i migliori?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Head profile */}
                <div>
                  <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-1.5"><Crown className="h-4 w-4" /> I tuoi {head.length} clienti migliori (Testa)</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-lg border p-3 text-center">
                      <div className="text-[10px] uppercase text-muted-foreground">Fatturato medio</div>
                      <div className="text-lg font-black text-emerald-700">{fmtCur(headAvgRevenue)}</div>
                    </div>
                    <div className="bg-white rounded-lg border p-3 text-center">
                      <div className="text-[10px] uppercase text-muted-foreground">Margine medio</div>
                      <div className="text-lg font-black text-emerald-700">{fmtCur(headAvgProfit)}</div>
                    </div>
                    <div className="bg-white rounded-lg border p-3 text-center">
                      <div className="text-[10px] uppercase text-muted-foreground">Margine ponderato</div>
                      <div className="text-lg font-black text-emerald-700">{fmtPct(headWeightedMargin)}</div>
                    </div>
                    <div className="bg-white rounded-lg border p-3 text-center">
                      <div className="text-[10px] uppercase text-muted-foreground">Fatturato totale</div>
                      <div className="text-lg font-black text-emerald-700">{fmtCur(headTotalRevenue)}</div>
                    </div>
                  </div>
                </div>

                <hr className="border-emerald-100" />

                {/* Body: current vs simulated */}
                <div>
                  <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-1.5"><Minus className="h-4 w-4" /> I {body.length} clienti del Corpo oggi</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead></TableHead>
                        <TableHead className="text-right">Fatturato totale</TableHead>
                        <TableHead className="text-right">Fatt. medio/cliente</TableHead>
                        <TableHead className="text-right">Margine totale</TableHead>
                        <TableHead className="text-right">Prof. medio/cliente</TableHead>
                        <TableHead className="text-right">Margine</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-semibold">Situazione attuale</TableCell>
                        <TableCell className="text-right tabular-nums font-bold">{fmtCur(bodyTotalRevenue)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtCur(bodyAvgRevenue)}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-amber-700">{fmtCur(bodyTotalProfit)}</TableCell>
                        <TableCell className="text-right tabular-nums text-amber-700">{fmtCur(bodyAvgProfit)}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-amber-700">{fmtPct(bodyWeightedMargin)}</TableCell>
                      </TableRow>
                      <TableRow className="bg-emerald-50">
                        <TableCell className="font-semibold text-emerald-800">Se diventassero Testa</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-emerald-700">{fmtCur(bodySimulatedRevenue)}</TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-700">{fmtCur(headAvgRevenue)}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-emerald-700">{fmtCur(bodySimulatedProfit)}</TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-700">{fmtCur(headAvgProfit)}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-emerald-700">{fmtPct(headWeightedMargin)}</TableCell>
                      </TableRow>
                      <TableRow className="bg-emerald-100/70">
                        <TableCell className="font-bold text-emerald-900">Differenza</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-emerald-800">{bodyRevenueGain >= 0 ? '+' : ''}{fmtCur(bodyRevenueGain)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right tabular-nums font-black text-emerald-800 text-lg">{bodyProfitGain >= 0 ? '+' : ''}{fmtCur(bodyProfitGain)}</TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-emerald-700">{(headWeightedMargin - bodyWeightedMargin) >= 0 ? '+' : ''}{fmtPct(headWeightedMargin - bodyWeightedMargin)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Impact on total */}
                <div className={`rounded-lg border p-4 flex items-center justify-between ${bodyProfitGain >= 0 ? 'bg-gradient-to-r from-emerald-100 to-emerald-50 border-emerald-300' : 'bg-gradient-to-r from-blue-50 to-slate-50 border-blue-200'}`}>
                  <div className="text-sm text-slate-800">
                    <strong>Margine totale azienda</strong> se ogni cliente del Corpo raggiungesse il livello medio della Testa ({fmtCur(headAvgProfit)}/cliente):
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-black ${bodyProfitGain >= 0 ? 'text-emerald-900' : 'text-slate-900'}`}>{fmtCur(simulatedTotalProfit)}</div>
                    <div className="text-xs text-slate-600">attuale: {fmtCur(totalProfit)} → {bodyProfitGain >= 0 ? '+' : ''}{fmtPct(bodyProfitGain / Math.abs(totalProfit || 1) * 100)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* === CONFRONTO TESTA / CORPO / CODA === */}
        {whaleData?.segments_comparison && (() => {
          const segs = whaleData.segments_comparison;
          const SEG_META = [
            { key: 'head', label: 'Testa', color: 'emerald', icon: Crown,
              operational: 'Clienti premium fedeli e poco sensibili al prezzo. Difenderli, ascoltarli, replicarli.' },
            { key: 'body', label: 'Corpo', color: 'amber', icon: Minus,
              operational: 'Zoccolo duro. Alza il listino del 10-15% solo su una selezione (non tutti). Identifica i prossimi candidati Testa.' },
            { key: 'tail', label: 'Coda', color: 'red', icon: Scissors,
              operational: 'Clienti in perdita strutturale. Rinegozia al primo aumento o lasciali andare: ti pagano per lavorare.' },
          ];
          return (
            <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50/40 via-white to-pink-50/30" data-testid="segments-comparison-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-purple-600" /> Perché i Testa sono Testa? Confronto tra i 3 segmenti
                </CardTitle>
                <CardDescription>
                  In una sola tabella vedi cosa fa davvero la differenza tra Testa, Corpo e Coda: <strong>il markup applicato sui costi variabili</strong>.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {SEG_META.map(({ key, label, color, icon: Icon, operational }) => {
                    const s = segs[key];
                    if (!s) return null;
                    const colorClasses = {
                      emerald: { border: 'border-emerald-300', bg: 'bg-emerald-50/70', text: 'text-emerald-700', accent: 'text-emerald-800' },
                      amber: { border: 'border-amber-300', bg: 'bg-amber-50/70', text: 'text-amber-700', accent: 'text-amber-800' },
                      red: { border: 'border-red-300', bg: 'bg-red-50/70', text: 'text-red-700', accent: 'text-red-800' },
                    }[color];
                    const netMarginClass = s.net_margin_pct < 0 ? 'text-red-700' : s.net_margin_pct >= 15 ? 'text-emerald-700' : 'text-amber-700';
                    return (
                      <div key={key} className={`rounded-lg border-2 ${colorClasses.border} ${colorClasses.bg} p-4`} data-testid={`segment-${key}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className={`flex items-center gap-2 font-bold ${colorClasses.accent}`}>
                            <Icon className="h-5 w-5" />
                            {label}
                          </div>
                          <div className={`text-2xl font-black ${colorClasses.text} tabular-nums`}>{s.count}</div>
                        </div>
                        <div className="text-[10px] text-slate-500 mb-2">
                          {s.salone_count} Interno · {s.vienna_count} Vienna
                        </div>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between border-b border-slate-200/60 pb-1">
                            <span className="text-slate-600">Clienti nel segmento</span>
                            <span className="font-semibold tabular-nums">{s.count}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-200/60 pb-1">
                            <span className="text-slate-600">Fatturato medio/cliente</span>
                            <span className="font-semibold tabular-nums">{fmtCur(s.avg_revenue)}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-200/60 pb-1">
                            <span className="text-slate-600">Fatturato totale</span>
                            <span className="font-semibold tabular-nums text-slate-700">{fmtCur(s.total_revenue)}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-200/60 pb-1">
                            <span className="text-slate-600">N° fatture/cliente</span>
                            <span className="font-semibold tabular-nums">{s.avg_invoices_per_customer != null ? s.avg_invoices_per_customer.toFixed(1) : '—'}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-200/60 pb-1">
                            <span className="text-slate-600 font-semibold">💰 Ticket medio/fattura</span>
                            <span className={`font-black tabular-nums text-base ${colorClasses.text}`}>{s.avg_revenue_per_invoice != null ? fmtCur(s.avg_revenue_per_invoice) : '—'}</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-200/60 pb-1">
                            <span className="text-slate-600">% Costi fissi assorbiti</span>
                            <span className="font-semibold tabular-nums text-slate-500">{s.fc_absorbed_pct != null ? fmtPct(s.fc_absorbed_pct) : '—'}</span>
                          </div>
                          <div className="flex justify-between pt-1">
                            <span className="text-slate-700 font-semibold">Margine netto</span>
                            <span className={`font-black tabular-nums text-lg ${netMarginClass}`}>{s.net_margin_pct != null ? fmtPct(s.net_margin_pct) : '—'}</span>
                          </div>
                        </div>
                        <div className={`mt-3 pt-2 border-t border-slate-200/60 text-[11px] text-slate-600 italic`}>
                          {operational}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 rounded-md bg-purple-50/60 border border-purple-200 p-3 text-sm text-slate-800">
                  <strong className="text-purple-900">💡 Cosa raccontano questi numeri:</strong>
                  {' '}I clienti TESTA hanno un ticket medio di <strong>{segs.head?.avg_revenue_per_invoice ? fmtCur(segs.head.avg_revenue_per_invoice) : '—'}</strong> per fattura e generano un margine del <strong>{segs.head?.net_margin_pct?.toFixed(1)}%</strong>.
                  {segs.body && (
                    <> Nel CORPO scende a <strong>{fmtCur(segs.body.avg_revenue_per_invoice || 0)}</strong>/fattura e margine <strong>{segs.body.net_margin_pct?.toFixed(1)}%</strong>.</>
                  )}
                  {segs.tail && (
                    <> Nella CODA i clienti emettono in media <strong>{segs.tail.avg_invoices_per_customer?.toFixed(1)}</strong> fatture all'anno per soli <strong>{fmtCur(segs.tail.avg_revenue_per_invoice || 0)}</strong>/fattura → margine <strong className="text-red-700">{segs.tail.net_margin_pct?.toFixed(1)}%</strong> (distruzione di valore: i costi amministrativi superano il ricavo).</>
                  )}
                  <br />
                  <span className="text-purple-900 font-semibold">📌 Leva operativa:</span> il problema della Coda non è il prezzo basso, è il <strong>n° eccessivo di fatture per piccoli importi</strong>. Riduci la frequenza di fatturazione o aumenta il ticket minimo per assorbire i costi amministrativi.
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* === CLIENTI SOTTO SOGLIA TICKET MINIMO === */}
        {whaleData?.summary?.customers_below_threshold > 0 && (() => {
          const sum = whaleData.summary;
          const threshold = sum.min_ticket_threshold || 250;
          const belowList = (whaleData.customers || []).filter(c => c.below_min_ticket);
          const shortfall = sum.shortfall_below_threshold || 0;
          const firstMarginBelow = sum.first_margin_below_threshold || 0;
          const revenueBelow = sum.revenue_below_threshold || 0;
          return (
            <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50/60 via-white to-orange-50/30" data-testid="below-threshold-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="text-xl">⚠️</span> Clienti sotto soglia minima sostenibile
                </CardTitle>
                <CardDescription>
                  Soglia minima: <strong>{fmtCur(threshold)}/fattura</strong>. Sotto questa soglia il ticket non copre i costi amministrativi (sede, gestionale, contabilità) generati dall'emissione della fattura.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* PANNELLO KPI: Break-even amministrativo */}
                <div className="rounded-xl border-2 border-violet-300 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 p-4 mb-4">
                  <div className="text-xs uppercase tracking-wide font-bold text-violet-700 mb-2">
                    📐 Break-Even Amministrativo per Fattura
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="bg-white/70 rounded-lg p-3 border border-violet-200">
                      <div className="text-[10px] uppercase text-slate-600 font-semibold">Costi Fissi periodo</div>
                      <div className="text-2xl font-black text-violet-700 tabular-nums mt-1">{fmtCur(sum.total_fixed_costs_period)}</div>
                    </div>
                    <div className="bg-white/70 rounded-lg p-3 border border-violet-200">
                      <div className="text-[10px] uppercase text-slate-600 font-semibold">N° Fatture (interventi)</div>
                      <div className="text-2xl font-black text-violet-700 tabular-nums mt-1">{sum.total_invoices || 0}</div>
                    </div>
                    <div className="bg-violet-100 rounded-lg p-3 border-2 border-violet-400">
                      <div className="text-[10px] uppercase text-violet-800 font-semibold">Costi Fissi per fattura</div>
                      <div className="text-2xl font-black text-violet-800 tabular-nums mt-1">{fmtCur(sum.fc_per_invoice_breakeven)}</div>
                      <div className="text-[10px] text-violet-700 mt-0.5">ogni fattura DEVE coprire</div>
                    </div>
                    <div className="bg-emerald-100 rounded-lg p-3 border-2 border-emerald-400">
                      <div className="text-[10px] uppercase text-emerald-800 font-semibold">Ticket BE completo</div>
                      <div className="text-2xl font-black text-emerald-800 tabular-nums mt-1">{fmtCur(sum.breakeven_ticket)}</div>
                      <div className="text-[10px] text-emerald-700 mt-0.5">Costi Fissi + Costi Variabili ({sum.vc_pct_of_revenue?.toFixed(1)}%)</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    📊 In sintesi: con <strong>{fmtCur(sum.total_fixed_costs_period)}</strong> di costi fissi e <strong>{sum.total_invoices}</strong> fatture emesse,
                    ogni intervento dovrebbe coprire <strong className="text-violet-800">{fmtCur(sum.fc_per_invoice_breakeven)}</strong> di costi fissi.
                    Sommando i costi variabili ({sum.vc_pct_of_revenue?.toFixed(1)}%), il <strong>ticket minimo matematico per il break-even è {fmtCur(sum.breakeven_ticket)}/fattura</strong>.
                    Hai scelto come soglia obiettivo realistico <strong>{fmtCur(threshold)}/fattura</strong>.
                  </p>
                </div>

                {/* 2 LEVE OPERATIVE */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-3">
                    <div className="text-xs font-bold text-blue-900 uppercase mb-1">🎯 Leva 1: aumentare i prezzi</div>
                    <p className="text-sm text-blue-900 leading-snug">
                      I <strong>{sum.customers_below_threshold} clienti</strong> sotto {fmtCur(threshold)}/fattura
                      hanno una <strong>sotto-copertura</strong> totale di <strong className="text-amber-800">{fmtCur(shortfall)}/anno</strong> rispetto alla loro quota di costi fissi.
                      Rinegoziarli alla soglia eliminerebbe questo gap.
                    </p>
                  </div>
                  <div className="rounded-lg border-2 border-purple-300 bg-purple-50 p-3">
                    <div className="text-xs font-bold text-purple-900 uppercase mb-1">✂️ Leva 2: ridurre i costi fissi</div>
                    <p className="text-sm text-purple-900 leading-snug">
                      Per rendere sostenibile il ticket di {fmtCur(threshold)}/fattura senza aumentare i prezzi,
                      dovresti tagliare i costi fissi di <strong className="text-red-700">{fmtCur(sum.fc_reduction_needed)}</strong>
                      <span className="ml-1">(<strong>−{sum.fc_reduction_pct_needed?.toFixed(0)}%</strong>)</span>,
                      portandoli da {fmtCur(sum.total_fixed_costs_period)} a <strong>{fmtCur(sum.target_total_fc_for_threshold)}</strong>.
                    </p>
                  </div>
                </div>

                {/* AVVISO IMPORTANTE: Sotto-copertura ≠ perdita cash */}
                <div className="rounded-lg border-2 border-orange-300 bg-orange-50/70 p-3 mb-4">
                  <div className="text-xs font-bold text-orange-900 uppercase mb-1.5">⚠️ Attenzione: sotto-copertura ≠ perdita di bilancio</div>
                  <p className="text-sm text-orange-900 leading-snug">
                    La <strong>sotto-copertura {fmtCur(shortfall)}</strong> indica solo "quanto manca a questi clienti per coprire la loro quota di costi fissi".
                    <strong className="text-red-800"> NON è cash perso e NON coincide con la perdita di bilancio.</strong> Anzi:
                    questi 94 clienti generano <strong className="text-emerald-700">{fmtCur(firstMarginBelow)}</strong> di contribuzione gross ai costi fissi
                    (= ricavo {fmtCur(revenueBelow)} − costi variabili).
                    <br />
                    👉 <strong>Effetto sul bilancio se li eliminassi senza ridurre i costi fissi: −{fmtCur(firstMarginBelow)}/anno</strong> (perderesti la loro contribuzione mentre i costi fissi restano).
                    Tagliarli ha senso solo se riduci proporzionalmente la struttura.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Clienti sotto soglia</div>
                    <div className="text-3xl font-black text-amber-700 tabular-nums mt-1">{sum.customers_below_threshold}</div>
                    <div className="text-[11px] text-amber-700 mt-1">su {sum.total_customers} totali ({((sum.customers_below_threshold/sum.total_customers)*100).toFixed(0)}%)</div>
                  </div>
                  <div className="rounded-lg border border-orange-300 bg-orange-50 p-3" title="Quanto manca a questi clienti per coprire la loro quota di costi fissi (non è perdita di cassa)">
                    <div className="text-[10px] uppercase tracking-wide text-orange-700 font-semibold">Sotto-copertura Costi Fissi</div>
                    <div className="text-3xl font-black text-orange-700 tabular-nums mt-1">-{fmtCur(shortfall)}</div>
                    <div className="text-[11px] text-orange-700 mt-1">vs quota ideale (≠ perdita)</div>
                  </div>
                  <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Contribuzione ai Costi Fissi</div>
                    <div className="text-3xl font-black text-emerald-700 tabular-nums mt-1">+{fmtCur(firstMarginBelow)}</div>
                    <div className="text-[11px] text-emerald-700 mt-1">apporto positivo (ricavo − costi variabili)</div>
                  </div>
                </div>

                <div className="overflow-auto border rounded-md" style={{ maxHeight: '60vh' }}>
                  <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 z-20 bg-slate-100 shadow-[0_2px_0_rgba(0,0,0,0.08)]">
                      <tr className="border-b">
                        <th className="text-left font-semibold text-slate-700 px-2 py-2 bg-slate-100">Cliente</th>
                        <th className="text-right font-semibold text-slate-700 px-2 py-2 bg-slate-100">N° fatture</th>
                        <th className="text-right font-semibold text-slate-700 px-2 py-2 bg-slate-100">Fatturato anno</th>
                        <th className="text-right font-semibold text-slate-700 px-2 py-2 bg-slate-100">Ticket attuale</th>
                        <th className="text-right font-semibold text-slate-700 px-2 py-2 bg-slate-100">Ticket minimo</th>
                        <th className="text-right font-semibold text-slate-700 px-2 py-2 bg-slate-100">Aumento %</th>
                        <th className="text-right font-semibold text-slate-700 px-2 py-2 bg-slate-100">Perdita attuale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {belowList.sort((a, b) => (a.profit || 0) - (b.profit || 0)).map((c) => (
                        <tr key={c.id} className="border-b hover:bg-slate-50" data-testid={`below-row-${c.id}`}>
                          <td className="font-medium px-2 py-1.5">{c.name}</td>
                          <td className="text-right tabular-nums px-2 py-1.5">{c.n_invoices || 0}</td>
                          <td className="text-right tabular-nums px-2 py-1.5">{fmtCur(c.revenue)}</td>
                          <td className="text-right tabular-nums font-semibold text-amber-700 px-2 py-1.5">{c.avg_ticket != null ? fmtCur(c.avg_ticket) : '—'}</td>
                          <td className="text-right tabular-nums text-slate-600 px-2 py-1.5">{fmtCur(threshold)}</td>
                          <td className="text-right tabular-nums font-bold text-purple-700 px-2 py-1.5">+{c.ticket_increase_pct != null ? c.ticket_increase_pct.toFixed(0) : '—'}%</td>
                          <td className="text-right tabular-nums font-bold text-red-700 px-2 py-1.5">{fmtCur(c.profit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="text-[12px] text-slate-700 mt-3 leading-relaxed bg-blue-50/40 border border-blue-200 rounded-md p-3">
                  💡 <strong className="text-blue-900">Come usare questa tabella</strong>:
                  ordina le righe per "perdita" e parti dai clienti con più perdita assoluta. Per ciascuno valuta:
                  <ol className="ml-5 mt-1 list-decimal space-y-0.5">
                    <li><strong>Rinegoziare il prezzo</strong> al ticket minimo di {fmtCur(threshold)}/fattura (se rapporto continuativo)</li>
                    <li><strong>Consolidare le fatture</strong>: passare da N fatture a 1 fattura/anno taglia i costi amministrativi (es. da 4 a 1 fattura: -75% costi fissi allocati)</li>
                    <li><strong>Cessare il rapporto</strong> se nessuna delle prime due è praticabile</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          );
        })()}
      </>)}

      <CustomerFormDialog open={showForm} onClose={() => { setShowForm(false); setEditCustomer(null); }} onSave={saveCustomer} customer={editCustomer} hourlyRate={whaleData?.summary?.hourly_rate} />
      <ImportDialog open={showImport} onClose={() => setShowImport(false)} clientId={clientId} onImportDone={fetchAll} selectedYear={selectedYear} />
    </div>
  );
};

export default WhaleCurvePage;
